import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { MetricsLabel, MetricsMainContainer, MetricsPageContent, MetricsPageSetting, MetricsPageSubtitle, MetricsPageTable, MetricsPageTableWrapper, MetricsPageTitle, MetricsSearch, MetricsSearchList, MetricsSettingsField, MetricsTableLegend, MetricsTableTooltip } from "./styles"
import { CloseIcon, SearchIcon } from "shared/icons"
import MetricsDropdown from "./Dropdown"
import CheckLongreadRadioButton from "../teams/components/CheckLongreadRadioButton"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { bProfile, useProfile } from "@brg/auth"
import { Avatar, BTag, ExternalLink, FInter, FlexBox, Tooltip, useBreakpoint } from "@brg/ui"
import { useGetCurrentAndPreviousUsersProjectTags, useGetDisciplines, useGetEduTags, useGetMetrics, useGetStrudentsRating, useGetTeachersRatings } from "shared/hooks/metricsPageHooks"
import { NavbarResultsItem, useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar"
import { API, Designer, DesignerAPI } from "@brg/api"
import InfiniteScroll from "react-infinite-scroll-component"
import { computeDesigner } from "shared/helpers/computeData"

const MetricsRadioButtons = styled.div<any>`
    display: flex;
    flex-direction: ${p => p.direction || 'column'};
    gap: 16px;
    height: 100%;
    ${p => p.align && 'align-items: ' + p.align + ';'} 
`

const MetricsRadio = styled.div`
    white-space: nowrap;
    .radio-button--custom-radio {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        ::after {
            height: 5px;
            width: 5px;
        }
    }
`

const InfoIcon = () => {
    return  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.375" y="0.375" width="11.25" height="11.25" rx="5.625" stroke="#909090" stroke-width="0.75"/>
    <path d="M5.34345 8.00568V7.96413C5.34807 7.52326 5.39423 7.17241 5.48194 6.91158C5.56966 6.65075 5.6943 6.43954 5.85588 6.27797C6.01746 6.11639 6.21135 5.96751 6.43756 5.83132C6.57374 5.74822 6.69608 5.65012 6.80457 5.53702C6.91305 5.42161 6.99846 5.28888 7.06078 5.13885C7.12541 4.98881 7.15773 4.82262 7.15773 4.64027C7.15773 4.41406 7.10464 4.21786 6.99846 4.05167C6.89228 3.88548 6.75032 3.75737 6.57259 3.66735C6.39485 3.57733 6.1975 3.53231 5.98052 3.53231C5.79125 3.53231 5.6089 3.57156 5.43347 3.65004C5.25805 3.72852 5.11147 3.85201 4.99375 4.02051C4.87603 4.18901 4.80794 4.40945 4.78947 4.68182H3.91696C3.93542 4.28942 4.03699 3.95357 4.22165 3.67427C4.40861 3.39498 4.65444 3.18146 4.95913 3.03374C5.26612 2.88601 5.60659 2.81214 5.98052 2.81214C6.38677 2.81214 6.73993 2.89293 7.04001 3.05451C7.34239 3.21609 7.57552 3.43768 7.7394 3.71928C7.9056 4.00089 7.98869 4.32173 7.98869 4.68182C7.98869 4.93572 7.94945 5.16539 7.87097 5.37083C7.7948 5.57626 7.684 5.75977 7.53859 5.92134C7.39547 6.08292 7.22236 6.22603 7.01923 6.35067C6.81611 6.47763 6.65338 6.61151 6.53104 6.75231C6.4087 6.8908 6.31984 7.05584 6.26444 7.24743C6.20904 7.43901 6.17903 7.67791 6.17442 7.96413V8.00568H5.34345ZM5.78663 10.0554C5.61582 10.0554 5.46925 9.99423 5.34691 9.87189C5.22458 9.74956 5.16341 9.60298 5.16341 9.43217C5.16341 9.26136 5.22458 9.11479 5.34691 8.99245C5.46925 8.87012 5.61582 8.80895 5.78663 8.80895C5.95744 8.80895 6.10402 8.87012 6.22635 8.99245C6.34869 9.11479 6.40986 9.26136 6.40986 9.43217C6.40986 9.54528 6.381 9.64915 6.3233 9.74379C6.2679 9.83842 6.19288 9.9146 6.09824 9.9723C6.00591 10.0277 5.90204 10.0554 5.78663 10.0554Z" fill="#909090"/>
    </svg>
}

const teachersRatingDisciplineTypes: any = {
    'personal': 5,
    'students': 6
}

const MetricsPage: FC = (props: any) => {

    const [studentsSearch, setStudentSearch] = useState('')
    const [studentsList, setStudentsList] = useState<any[]>([])
    const [showStudentsList, setShowStudentsList] = useState(false)
    const [student, setStudent] = useState<any>()
    const [timePeriod, setTimePeriod]: any = useState(0)
    const [metricsType, setMetricsType]: any = useState('personal')
    const [page, setPage] = useState(1)

    const limit = 30

    const isMobile = useBreakpoint(743)

    const scrollRef = useRef<any>(null)

    const { t, i18n } = useTranslation()
    const {language} = i18n
    const profile = useProfile()

    const isAuthorized = profile && (bProfile.isModer(profile) || bProfile.isStudent(profile) || bProfile.isEmployee(profile) || profile?.globalTypes?.includes('Cooperator'))

    const metrics = useGetMetrics(language, isAuthorized, 'teachers')

    const disciplines = useGetDisciplines(language, isAuthorized, 'teachers')
    
    const disciplinesList = useMemo(() => {
        const list: any = {}
        disciplines.forEach((discipline: any) => {
            list[discipline.disciplineType] = discipline.name;
        })
        return list
    }, [disciplines])

    const [timePeriods, periodsDropdown] = useMemo(() => {
        const pickedDiscipline: any = disciplines?.find((item : any) => {
            return Number(item.disciplineType) === Number(teachersRatingDisciplineTypes[metricsType])
        })

        const periodsArr: any = pickedDiscipline?.periods

        const periodsDropdown: any = {}
        for (let i in periodsArr) {
            periodsDropdown[i] = periodsArr[i]
        }
        return [periodsArr, periodsDropdown]
    }, [disciplinesList, metricsType])
    
    const ratingsQuery = useMemo(() => {
        return {
            intervalType: periodsDropdown?.[timePeriod]?.timeIntervalType,
            date: periodsDropdown?.[timePeriod]?.date,
            disciplineType: teachersRatingDisciplineTypes[metricsType],
            limit: limit,
            page: page,
            userId: student ? student : profile ? profile?.accountId : null
        }
    }, 
    [
        profile,
        student,
        periodsDropdown,
        timePeriod, 
        page,
        limit
    ])

    const filteredMetrics = useMemo(() => {
        return metrics.filter((metric: any) => metric.disciplineType === teachersRatingDisciplineTypes[metricsType])
    }, [metrics, metricsType])

    const [teachers, paging, requestedUser] = useGetTeachersRatings(ratingsQuery, language, isAuthorized)

    const isLastPage = useMemo(() => {
        return page * limit >= paging?.count
    }, [page, limit, paging])

    const setMetricsTypeCB = useCallback((newType: string) => () => {
        setMetricsType(newType)
        setPage(1)
        setTimePeriod(0)
    }, []) 

    const setTimePeriodCB = useCallback((newPeriod: any) => {
        setPage(1)
        setTimePeriod(newPeriod)
    }, [])

    const pickStudent = useCallback((student: any) => () => {
        setStudent(student.accountId)
        setStudentSearch(student.fullName)
        // setPage(1)
        setShowStudentsList(false)
    }, [])

    const clearStudentsSearch = useCallback(() => {
        setStudentSearch('')
        setStudent(null)
    }, [])

    const isRequestedStudentNotFound = useMemo(() => {
        if (student) {
            if (!requestedUser) {
                return true
            }
        }
        return false
    }, [studentsSearch, requestedUser])

    const fetchDesigners = async () => {
    //   setShowStudentsList(false)
      DesignerAPI.AutoComplete({ key: studentsSearch})
      .then((v) => {
        // setShowStudentsList(true)
        return v.json()
      })
      .then((v) => API.toLang<Designer[]>(v, i18n.language))
      .then((v) => {
        setStudentsList(v);
        // setIsEmpty(v.length < 1);
      })
    }

    // useEffect(() => {
    //     if (!blackTagSearch) setBlackTagSearch(blackTag?.name || blackTags?.[0]?.name)
    // }, [blackTags])

    useDebouncedEffect(
        () => {
          if (studentsSearch.length >= 2) {
            fetchDesigners();
          } else {
            setStudentsList([]);
          }
        },
        [studentsSearch],
        700
      );

    // const {requestedUserWithPosition: requestedUser} = students || {}
    // const {studentsWithRating} = students || {}

    const tableRowRender = useMemo(() => {

    }, [])

    return <MetricsMainContainer>
        <MetricsPageTitle>
            Оценка профессиональной активности
        </MetricsPageTitle>
        <MetricsPageSubtitle>
            <span>
                Подробная информация о рассчете
            </span> 
            <InfoIcon />
        </MetricsPageSubtitle>
        {
            isAuthorized?
            <MetricsPageContent direction={'column'} gap={16}>
                <MetricsPageSetting width={'100%'} direction={'row'} offsetBottom={0}>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Категории
                        </MetricsLabel>
                        <MetricsRadioButtons align={'center'} direction={'row'}>
                            <MetricsSettingsField>
                                <MetricsRadio>
                                    <CheckLongreadRadioButton
                                        onChange={setMetricsTypeCB('personal')}
                                        name="metricsType"
                                        title={'личные достижения'}
                                        checked={metricsType === 'personal'}
                                    />
                                </MetricsRadio>
                                
                            </MetricsSettingsField>
                            <MetricsSettingsField>
                                <MetricsRadio>
                                    <CheckLongreadRadioButton
                                        onChange={setMetricsTypeCB('students')}
                                        name="metricsType"
                                        title={'работа со студентами'}
                                        checked={metricsType === 'students'}
                                    />
                                </MetricsRadio>
                            </MetricsSettingsField>
                        </MetricsRadioButtons>
                    </MetricsSettingsField>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Период
                        </MetricsLabel>
                        <MetricsDropdown 
                            dropdownValue={periodsDropdown?.[timePeriod]?.periodName}
                            valuesList={periodsDropdown}
                            setDropdownValue={setTimePeriodCB}
                            // disableChange={Number(disciplineIndex) === 4}
                        />
                    </MetricsSettingsField>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Поиск студента по имени
                        </MetricsLabel>
                        <MetricsSearch>
                            <SearchIcon/>
                            <input 
                                type="text" 
                                value={studentsSearch} 
                                onChange={(e) => setStudentSearch(e.target.value)}
                                placeholder="поиск по участникам"
                                onFocus={() => setShowStudentsList(true)}
                                onBlur={() => setShowStudentsList(false)}
                            />
                            <CloseIcon className='clear-icon' onClick={clearStudentsSearch} />
                            {showStudentsList && <MetricsSearchList>
                                {
                                studentsSearch.length < 2 ?
                                <></> 
                                :
                                !studentsList?.length ?
                                <NavbarResultsItem>
                                <FlexBox align={"center"} gx={8} offsetLeft={16}>
                                    <FInter.SmallTextDiv>{'студентов по запросу не найдено'}</FInter.SmallTextDiv>
                                </FlexBox>
                                </NavbarResultsItem>
                                :
                                studentsList.map((student: any) => {
                                    const cv = computeDesigner(student);
                                    return (
                                        <NavbarResultsItem
                                        onMouseDown={pickStudent(cv)}
                                        key={cv.accountId}
                                        >
                                        <FlexBox align={"center"} gx={8}>
                                            <FlexBox width={52} height={52}>
                                            <Avatar {...cv} />
                                            </FlexBox>
                                            <FInter.SmallTextDiv>{cv.fullName}</FInter.SmallTextDiv>
                                        </FlexBox>
                                        </NavbarResultsItem>
                                    )
                                })}
                            </MetricsSearchList>}
                        </MetricsSearch>
                    </MetricsSettingsField>
                </MetricsPageSetting>
                <InfiniteScroll
                        //@ts-ignore
                            dataLength={teachers.length}
                            next={() => {if (!isLastPage) setPage(page + 1)}}
                            hasMore={!isLastPage}
                            initialScrollY={0}
                            scrollThreshold={0.9}
                            loader={<></>}
                            ref={scrollRef}
                        >
                
                <MetricsPageTableWrapper>
                    <MetricsTableLegend>
                        <div><span className="bold">{paging?.count !== undefined? paging?.count : 0}</span> <span>Студентов в рейтинге</span></div>
                        <div><div className="winner" /> <span>Победители</span></div>
                        <div><div className="short-list" /> <span>Шорт-лист</span></div>
                    </MetricsTableLegend>
                    <FlexBox>

                    <MetricsPageTable className="metrics_teachers-table">
                        <tr>
                            <th><div>№</div></th>
                            <th>ФИО</th>
                        </tr>
                        {(requestedUser || isRequestedStudentNotFound) && <>
                            <tr className={`${requestedUser?.groupType === 1 ? 'winner ' : requestedUser?.groupType === 2? 'short-list ' : ''}user-row`}>
                                <td>{requestedUser?.positionInRating || ''}</td>
                                <td className="metrics_name-cell">
                                    {
                                        isRequestedStudentNotFound?
                                            'Не найдено'
                                        :
                                        <ExternalLink to={`/designer/${requestedUser?.userId}`} target="_blank">
                                            {`${requestedUser?.surname} ${requestedUser?.name || ''}`}
                                        </ExternalLink>
                                    }
                                </td>
                            </tr>
                            <tr className="empty-row"></tr>
                        </>}
                        {
                            !!teachers?.length ?
                                teachers.map((student: any, index: number) => {
                                    const details = student.ratingForMetric
                                    return <tr key={student.userId} className={`${student.userId === requestedUser?.userId? 'bold ' : ''}${student.groupType === 1 ? 'winner' : student.groupType === 2? 'short-list' : ''}`}>
                                        <td>{student.positionInRating || index + 1 || ''}</td>
                                        <td className="metrics_name-cell"><ExternalLink to={`/designer/${student?.userId}`} target="_blank">{`${student.surname} ${student.name || ''}`}</ExternalLink></td>
                                    </tr>
                                })
                            :
                            <tr>
                                <td></td>
                                <td></td>
                            </tr>
                        }
                    </MetricsPageTable>

                    <MetricsPageTable className="table-scrollable metrics_teachers-table">
                        <tr>
                            <th>Рейтинг в категории</th>
                            {!!filteredMetrics?.length && filteredMetrics.map((metricType: any) => {
                                return <th key={metricType.id + '1'}>{metricType.name}</th>
                            })}                 
                        </tr>
                        {(requestedUser || isRequestedStudentNotFound) && <>
                            <tr className={`${requestedUser?.groupType === 1 ? 'winner ' : requestedUser?.groupType === 2? 'short-list ' : ''}user-row`}>
                                <td>{requestedUser?.rating || (isRequestedStudentNotFound? '' : '0')}</td>
                                {!!filteredMetrics.length && filteredMetrics.map((metricsType: any) => {
                                    const ratingValue = requestedUser?.ratingForMetric?.find((ratingMetric: any) => {
                                            return ratingMetric.metricName === metricsType.name
                                        })?.rating || '0'
                                    return <td key={metricsType.id + '2'}>
                                        {isRequestedStudentNotFound? '' : ratingValue}
                                    </td>
                                })}
                            </tr>
                            <tr className="empty-row"></tr>
                        </>}
                        {
                            !!teachers?.length ?
                                teachers.map((student: any, index: number) => {
                                    const details = student.ratingForMetric
                                    return <tr key={student.userId + '1'} className={`${student.userId === requestedUser?.userId? 'bold ' : ''}${student.groupType === 1 ? 'winner' : student.groupType === 2? 'short-list' : ''}`}>
                                        <Tooltip
                                            // visible={true}
                                            target={
                                                <td>{student.rating || '0'}</td>
                                            }
                                            placement="top"
                                            offset={[0, -5]}
                                        >       
                                            <MetricsTableTooltip>
                                                Рейтинг в категории
                                            </MetricsTableTooltip>     
                                        </Tooltip>
                                        {!!filteredMetrics.length && filteredMetrics.map((metricsType: any) => {
                                            const ratingValue = student.ratingForMetric?.find((ratingMetric: any) => {
                                                    return ratingMetric.metricName === metricsType.name
                                                })?.rating || '0'
                                            return <Tooltip
                                                // visible={true}
                                                target={
                                                <td key={metricsType.id + '2'}>
                                                    {ratingValue}
                                                </td>}
                                                placement="top"
                                                offset={[0, -5]}
                                            >       
                                                <MetricsTableTooltip>
                                                    {metricsType.name}
                                                </MetricsTableTooltip>     
                                            </Tooltip>
                                        })}
                                    </tr>
                                })
                            :
                            <tr>
                                <td colSpan={metrics?.length || 10}>Нет данных по выбранному значению</td>
                            </tr>
                        }
                    </MetricsPageTable>
                    </FlexBox>
                </MetricsPageTableWrapper>
                </InfiniteScroll>
            </MetricsPageContent>
            :
            <div>
                {t('not_authorized_msg')}
            </div>
        }
    </MetricsMainContainer>
}

export default memo(MetricsPage)import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { MetricsLabel, MetricsMainContainer, MetricsPageContent, MetricsPageSetting, MetricsPageSubtitle, MetricsPageTable, MetricsPageTableWrapper, MetricsPageTitle, MetricsSearch, MetricsSearchList, MetricsSettingsField, MetricsTableLegend, MetricsTableTooltip } from "./styles"
import { CloseIcon, SearchIcon } from "shared/icons"
import MetricsDropdown from "./Dropdown"
import CheckLongreadRadioButton from "../teams/components/CheckLongreadRadioButton"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { bProfile, useProfile } from "@brg/auth"
import { Avatar, BTag, ExternalLink, FInter, FlexBox, Tooltip, useBreakpoint } from "@brg/ui"
import { useGetCurrentAndPreviousUsersProjectTags, useGetDisciplines, useGetEduTags, useGetMetrics, useGetStrudentsRating, useGetTeachersRatings } from "shared/hooks/metricsPageHooks"
import { NavbarResultsItem, useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar"
import { API, Designer, DesignerAPI } from "@brg/api"
import InfiniteScroll from "react-infinite-scroll-component"
import { computeDesigner } from "shared/helpers/computeData"

const MetricsRadioButtons = styled.div<any>`
    display: flex;
    flex-direction: ${p => p.direction || 'column'};
    gap: 16px;
    height: 100%;
    ${p => p.align && 'align-items: ' + p.align + ';'} 
`

const MetricsRadio = styled.div`
    white-space: nowrap;
    .radio-button--custom-radio {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        ::after {
            height: 5px;
            width: 5px;
        }
    }
`

const InfoIcon = () => {
    return  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.375" y="0.375" width="11.25" height="11.25" rx="5.625" stroke="#909090" stroke-width="0.75"/>
    <path d="M5.34345 8.00568V7.96413C5.34807 7.52326 5.39423 7.17241 5.48194 6.91158C5.56966 6.65075 5.6943 6.43954 5.85588 6.27797C6.01746 6.11639 6.21135 5.96751 6.43756 5.83132C6.57374 5.74822 6.69608 5.65012 6.80457 5.53702C6.91305 5.42161 6.99846 5.28888 7.06078 5.13885C7.12541 4.98881 7.15773 4.82262 7.15773 4.64027C7.15773 4.41406 7.10464 4.21786 6.99846 4.05167C6.89228 3.88548 6.75032 3.75737 6.57259 3.66735C6.39485 3.57733 6.1975 3.53231 5.98052 3.53231C5.79125 3.53231 5.6089 3.57156 5.43347 3.65004C5.25805 3.72852 5.11147 3.85201 4.99375 4.02051C4.87603 4.18901 4.80794 4.40945 4.78947 4.68182H3.91696C3.93542 4.28942 4.03699 3.95357 4.22165 3.67427C4.40861 3.39498 4.65444 3.18146 4.95913 3.03374C5.26612 2.88601 5.60659 2.81214 5.98052 2.81214C6.38677 2.81214 6.73993 2.89293 7.04001 3.05451C7.34239 3.21609 7.57552 3.43768 7.7394 3.71928C7.9056 4.00089 7.98869 4.32173 7.98869 4.68182C7.98869 4.93572 7.94945 5.16539 7.87097 5.37083C7.7948 5.57626 7.684 5.75977 7.53859 5.92134C7.39547 6.08292 7.22236 6.22603 7.01923 6.35067C6.81611 6.47763 6.65338 6.61151 6.53104 6.75231C6.4087 6.8908 6.31984 7.05584 6.26444 7.24743C6.20904 7.43901 6.17903 7.67791 6.17442 7.96413V8.00568H5.34345ZM5.78663 10.0554C5.61582 10.0554 5.46925 9.99423 5.34691 9.87189C5.22458 9.74956 5.16341 9.60298 5.16341 9.43217C5.16341 9.26136 5.22458 9.11479 5.34691 8.99245C5.46925 8.87012 5.61582 8.80895 5.78663 8.80895C5.95744 8.80895 6.10402 8.87012 6.22635 8.99245C6.34869 9.11479 6.40986 9.26136 6.40986 9.43217C6.40986 9.54528 6.381 9.64915 6.3233 9.74379C6.2679 9.83842 6.19288 9.9146 6.09824 9.9723C6.00591 10.0277 5.90204 10.0554 5.78663 10.0554Z" fill="#909090"/>
    </svg>
}

const teachersRatingDisciplineTypes: any = {
    'personal': 5,
    'students': 6
}

const MetricsPage: FC = (props: any) => {

    const [studentsSearch, setStudentSearch] = useState('')
    const [studentsList, setStudentsList] = useState<any[]>([])
    const [showStudentsList, setShowStudentsList] = useState(false)
    const [student, setStudent] = useState<any>()
    const [timePeriod, setTimePeriod]: any = useState(0)
    const [metricsType, setMetricsType]: any = useState('personal')
    const [page, setPage] = useState(1)

    const limit = 30

    const isMobile = useBreakpoint(743)

    const scrollRef = useRef<any>(null)

    const { t, i18n } = useTranslation()
    const {language} = i18n
    const profile = useProfile()

    const isAuthorized = profile && (bProfile.isModer(profile) || bProfile.isStudent(profile) || bProfile.isEmployee(profile) || profile?.globalTypes?.includes('Cooperator'))

    const metrics = useGetMetrics(language, isAuthorized, 'teachers')

    const disciplines = useGetDisciplines(language, isAuthorized, 'teachers')
    
    const disciplinesList = useMemo(() => {
        const list: any = {}
        disciplines.forEach((discipline: any) => {
            list[discipline.disciplineType] = discipline.name;
        })
        return list
    }, [disciplines])

    const [timePeriods, periodsDropdown] = useMemo(() => {
        const pickedDiscipline: any = disciplines?.find((item : any) => {
            return Number(item.disciplineType) === Number(teachersRatingDisciplineTypes[metricsType])
        })

        const periodsArr: any = pickedDiscipline?.periods

        const periodsDropdown: any = {}
        for (let i in periodsArr) {
            periodsDropdown[i] = periodsArr[i]
        }
        return [periodsArr, periodsDropdown]
    }, [disciplinesList, metricsType])
    
    const ratingsQuery = useMemo(() => {
        return {
            intervalType: periodsDropdown?.[timePeriod]?.timeIntervalType,
            date: periodsDropdown?.[timePeriod]?.date,
            disciplineType: teachersRatingDisciplineTypes[metricsType],
            limit: limit,
            page: page,
            userId: student ? student : profile ? profile?.accountId : null
        }
    }, 
    [
        profile,
        student,
        periodsDropdown,
        timePeriod, 
        page,
        limit
    ])

    const filteredMetrics = useMemo(() => {
        return metrics.filter((metric: any) => metric.disciplineType === teachersRatingDisciplineTypes[metricsType])
    }, [metrics, metricsType])

    const [teachers, paging, requestedUser] = useGetTeachersRatings(ratingsQuery, language, isAuthorized)

    const isLastPage = useMemo(() => {
        return page * limit >= paging?.count
    }, [page, limit, paging])

    const setMetricsTypeCB = useCallback((newType: string) => () => {
        setMetricsType(newType)
        setPage(1)
        setTimePeriod(0)
    }, []) 

    const setTimePeriodCB = useCallback((newPeriod: any) => {
        setPage(1)
        setTimePeriod(newPeriod)
    }, [])

    const pickStudent = useCallback((student: any) => () => {
        setStudent(student.accountId)
        setStudentSearch(student.fullName)
        // setPage(1)
        setShowStudentsList(false)
    }, [])

    const clearStudentsSearch = useCallback(() => {
        setStudentSearch('')
        setStudent(null)
    }, [])

    const isRequestedStudentNotFound = useMemo(() => {
        if (student) {
            if (!requestedUser) {
                return true
            }
        }
        return false
    }, [studentsSearch, requestedUser])

    const fetchDesigners = async () => {
    //   setShowStudentsList(false)
      DesignerAPI.AutoComplete({ key: studentsSearch})
      .then((v) => {
        // setShowStudentsList(true)
        return v.json()
      })
      .then((v) => API.toLang<Designer[]>(v, i18n.language))
      .then((v) => {
        setStudentsList(v);
        // setIsEmpty(v.length < 1);
      })
    }

    // useEffect(() => {
    //     if (!blackTagSearch) setBlackTagSearch(blackTag?.name || blackTags?.[0]?.name)
    // }, [blackTags])

    useDebouncedEffect(
        () => {
          if (studentsSearch.length >= 2) {
            fetchDesigners();
          } else {
            setStudentsList([]);
          }
        },
        [studentsSearch],
        700
      );

    // const {requestedUserWithPosition: requestedUser} = students || {}
    // const {studentsWithRating} = students || {}

    const tableRowRender = useMemo(() => {

    }, [])

    return <MetricsMainContainer>
        <MetricsPageTitle>
            Оценка профессиональной активности
        </MetricsPageTitle>
        <MetricsPageSubtitle>
            <span>
                Подробная информация о рассчете
            </span> 
            <InfoIcon />
        </MetricsPageSubtitle>
        {
            isAuthorized?
            <MetricsPageContent direction={'column'} gap={16}>
                <MetricsPageSetting width={'100%'} direction={'row'} offsetBottom={0}>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Категории
                        </MetricsLabel>
                        <MetricsRadioButtons align={'center'} direction={'row'}>
                            <MetricsSettingsField>
                                <MetricsRadio>
                                    <CheckLongreadRadioButton
                                        onChange={setMetricsTypeCB('personal')}
                                        name="metricsType"
                                        title={'личные достижения'}
                                        checked={metricsType === 'personal'}
                                    />
                                </MetricsRadio>
                                
                            </MetricsSettingsField>
                            <MetricsSettingsField>
                                <MetricsRadio>
                                    <CheckLongreadRadioButton
                                        onChange={setMetricsTypeCB('students')}
                                        name="metricsType"
                                        title={'работа со студентами'}
                                        checked={metricsType === 'students'}
                                    />
                                </MetricsRadio>
                            </MetricsSettingsField>
                        </MetricsRadioButtons>
                    </MetricsSettingsField>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Период
                        </MetricsLabel>
                        <MetricsDropdown 
                            dropdownValue={periodsDropdown?.[timePeriod]?.periodName}
                            valuesList={periodsDropdown}
                            setDropdownValue={setTimePeriodCB}
                            // disableChange={Number(disciplineIndex) === 4}
                        />
                    </MetricsSettingsField>
                    <MetricsSettingsField>
                        <MetricsLabel>
                            Поиск студента по имени
                        </MetricsLabel>
                        <MetricsSearch>
                            <SearchIcon/>
                            <input 
                                type="text" 
                                value={studentsSearch} 
                                onChange={(e) => setStudentSearch(e.target.value)}
                                placeholder="поиск по участникам"
                                onFocus={() => setShowStudentsList(true)}
                                onBlur={() => setShowStudentsList(false)}
                            />
                            <CloseIcon className='clear-icon' onClick={clearStudentsSearch} />
                            {showStudentsList && <MetricsSearchList>
                                {
                                studentsSearch.length < 2 ?
                                <></> 
                                :
                                !studentsList?.length ?
                                <NavbarResultsItem>
                                <FlexBox align={"center"} gx={8} offsetLeft={16}>
                                    <FInter.SmallTextDiv>{'студентов по запросу не найдено'}</FInter.SmallTextDiv>
                                </FlexBox>
                                </NavbarResultsItem>
                                :
                                studentsList.map((student: any) => {
                                    const cv = computeDesigner(student);
                                    return (
                                        <NavbarResultsItem
                                        onMouseDown={pickStudent(cv)}
                                        key={cv.accountId}
                                        >
                                        <FlexBox align={"center"} gx={8}>
                                            <FlexBox width={52} height={52}>
                                            <Avatar {...cv} />
                                            </FlexBox>
                                            <FInter.SmallTextDiv>{cv.fullName}</FInter.SmallTextDiv>
                                        </FlexBox>
                                        </NavbarResultsItem>
                                    )
                                })}
                            </MetricsSearchList>}
                        </MetricsSearch>
                    </MetricsSettingsField>
                </MetricsPageSetting>
                <InfiniteScroll
                        //@ts-ignore
                            dataLength={teachers.length}
                            next={() => {if (!isLastPage) setPage(page + 1)}}
                            hasMore={!isLastPage}
                            initialScrollY={0}
                            scrollThreshold={0.9}
                            loader={<></>}
                            ref={scrollRef}
                        >
                
                <MetricsPageTableWrapper>
                    <MetricsTableLegend>
                        <div><span className="bold">{paging?.count !== undefined? paging?.count : 0}</span> <span>Студентов в рейтинге</span></div>
                        <div><div className="winner" /> <span>Победители</span></div>
                        <div><div className="short-list" /> <span>Шорт-лист</span></div>
                    </MetricsTableLegend>
                    <FlexBox>

                    <MetricsPageTable className="metrics_teachers-table">
                        <tr>
                            <th><div>№</div></th>
                            <th>ФИО</th>
                        </tr>
                        {(requestedUser || isRequestedStudentNotFound) && <>
                            <tr className={`${requestedUser?.groupType === 1 ? 'winner ' : requestedUser?.groupType === 2? 'short-list ' : ''}user-row`}>
                                <td>{requestedUser?.positionInRating || ''}</td>
                                <td className="metrics_name-cell">
                                    {
                                        isRequestedStudentNotFound?
                                            'Не найдено'
                                        :
                                        <ExternalLink to={`/designer/${requestedUser?.userId}`} target="_blank">
                                            {`${requestedUser?.surname} ${requestedUser?.name || ''}`}
                                        </ExternalLink>
                                    }
                                </td>
                            </tr>
                            <tr className="empty-row"></tr>
                        </>}
                        {
                            !!teachers?.length ?
                                teachers.map((student: any, index: number) => {
                                    const details = student.ratingForMetric
                                    return <tr key={student.userId} className={`${student.userId === requestedUser?.userId? 'bold ' : ''}${student.groupType === 1 ? 'winner' : student.groupType === 2? 'short-list' : ''}`}>
                                        <td>{student.positionInRating || index + 1 || ''}</td>
                                        <td className="metrics_name-cell"><ExternalLink to={`/designer/${student?.userId}`} target="_blank">{`${student.surname} ${student.name || ''}`}</ExternalLink></td>
                                    </tr>
                                })
                            :
                            <tr>
                                <td></td>
                                <td></td>
                            </tr>
                        }
                    </MetricsPageTable>

                    <MetricsPageTable className="table-scrollable metrics_teachers-table">
                        <tr>
                            <th>Рейтинг в категории</th>
                            {!!filteredMetrics?.length && filteredMetrics.map((metricType: any) => {
                                return <th key={metricType.id + '1'}>{metricType.name}</th>
                            })}                 
                        </tr>
                        {(requestedUser || isRequestedStudentNotFound) && <>
                            <tr className={`${requestedUser?.groupType === 1 ? 'winner ' : requestedUser?.groupType === 2? 'short-list ' : ''}user-row`}>
                                <td>{requestedUser?.rating || (isRequestedStudentNotFound? '' : '0')}</td>
                                {!!filteredMetrics.length && filteredMetrics.map((metricsType: any) => {
                                    const ratingValue = requestedUser?.ratingForMetric?.find((ratingMetric: any) => {
                                            return ratingMetric.metricName === metricsType.name
                                        })?.rating || '0'
                                    return <td key={metricsType.id + '2'}>
                                        {isRequestedStudentNotFound? '' : ratingValue}
                                    </td>
                                })}
                            </tr>
                            <tr className="empty-row"></tr>
                        </>}
                        {
                            !!teachers?.length ?
                                teachers.map((student: any, index: number) => {
                                    const details = student.ratingForMetric
                                    return <tr key={student.userId + '1'} className={`${student.userId === requestedUser?.userId? 'bold ' : ''}${student.groupType === 1 ? 'winner' : student.groupType === 2? 'short-list' : ''}`}>
                                        <Tooltip
                                            // visible={true}
                                            target={
                                                <td>{student.rating || '0'}</td>
                                            }
                                            placement="top"
                                            offset={[0, -5]}
                                        >       
                                            <MetricsTableTooltip>
                                                Рейтинг в категории
                                            </MetricsTableTooltip>     
                                        </Tooltip>
                                        {!!filteredMetrics.length && filteredMetrics.map((metricsType: any) => {
                                            const ratingValue = student.ratingForMetric?.find((ratingMetric: any) => {
                                                    return ratingMetric.metricName === metricsType.name
                                                })?.rating || '0'
                                            return <Tooltip
                                                // visible={true}
                                                target={
                                                <td key={metricsType.id + '2'}>
                                                    {ratingValue}
                                                </td>}
                                                placement="top"
                                                offset={[0, -5]}
                                            >       
                                                <MetricsTableTooltip>
                                                    {metricsType.name}
                                                </MetricsTableTooltip>     
                                            </Tooltip>
                                        })}
                                    </tr>
                                })
                            :
                            <tr>
                                <td colSpan={metrics?.length || 10}>Нет данных по выбранному значению</td>
                            </tr>
                        }
                    </MetricsPageTable>
                    </FlexBox>
                </MetricsPageTableWrapper>
                </InfiniteScroll>
            </MetricsPageContent>
            :
            <div>
                {t('not_authorized_msg')}
            </div>
        }
    </MetricsMainContainer>
}

export default memo(MetricsPage)